// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
// --- Components
import { DetailsModal } from "tap/components/DetailsModal/index.jsx";
import { JobViewModalHeader } from "tap/components/JobViewModalHeader/index.jsx";
import { JobViewGrid } from "./grid";
import { PaginationBar } from "tap/components/PaginationBar/index.jsx";
import { tapApi } from "tap/api.js";
import { useLicensingJobsData } from "../../../hooks/useLicensingJobsData";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { useJobsActions } from "tap/store/licensingJobs/useJobs.js";
import { useStoredModalItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
import { normalizeLicensingJobViewTasks } from "tap/utils/normalizeLicensingJobViewTask.js";

export const JobStatusModal = () => {
  const {
    isJobViewModalOpen,
    jobViewTotalPages,
    jobViewCurrentPage,
    jobViewTotalItems,
    jobViewItemsPerPage,
    selectedJob,
    jobViewFilterForm,
    jobViewTasks,
    pendingStatuses,
    filterForm,
    licJobRange,
    licJobSort,
  } = useSelector((state) => state.licensingJobs);
  const { updateJobs, clearJobViewModal } = useJobsActions();
  const { fetchLicensingJobs } = useLicensingJobsData();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast } = useToasts();

  useStoredModalItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.licensingJobsJobView,
    isOpen: isJobViewModalOpen,
    itemsPerPage: jobViewItemsPerPage,
    onSync: updateJobs,
    itemsPerPageField: "jobViewItemsPerPage",
    currentPageField: "jobViewCurrentPage",
  });

  const onCloseModal = async () => {
    clearJobViewModal();

    const data = await fetchLicensingJobs(
      licJobRange,
      filterForm,
      licJobSort,
      true,
    );

    if (data) {
      updateJobs({
        jobs: data?.content,
        totalItems: data?.total,
        totalPages: Math.ceil(data?.total / data?.size) || 0,
        currentPage: data?.pageNumber,
      });
    }
  };

  const handlePageChange = async (pageNumber) => {
    setLoadingTrue();
    try {
      const apiPage = pageNumber + 1;
      const job = await tapApi.job(selectedJob?.id, true);
      const data = await tapApi.jobViewTasks(
        selectedJob?.id,
        true,
        apiPage,
        jobViewItemsPerPage,
        jobViewFilterForm.taskStatus.value,
      );

      if (
        data?.code ||
        data?.httpCode ||
        data?.error ||
        job?.code ||
        job?.httpCode ||
        job?.error
      ) {
        showErrorToast(data?.message || "Failed to fetch job tasks data.");
        return;
      }
      updateJobs({
        selectedJob: job,
        jobViewTasks: normalizeLicensingJobViewTasks(data?.tasks),
        jobViewTotalPages: data?.totalPages,
        jobViewTotalItems: data?.totalElements,
        jobViewCurrentPage: pageNumber,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to fetch job tasks data.");
      console.error("Error fetching job tasks:", error);
    } finally {
      setLoadingFalse();
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setLoadingTrue();
    try {
      const job = await tapApi.job(selectedJob?.id, true);
      const data = await tapApi.jobViewTasks(
        selectedJob?.id,
        true,
        1,
        newSize,
        jobViewFilterForm.taskStatus.value,
      );

      if (
        data?.code ||
        data?.httpCode ||
        data?.error ||
        job?.code ||
        job?.httpCode ||
        job?.error
      ) {
        showErrorToast(data?.message || "Failed to fetch job tasks data.");
        return;
      }
      updateJobs({
        selectedJob: job,
        jobViewTasks: normalizeLicensingJobViewTasks(data?.tasks),
        jobViewTotalPages: data?.totalPages,
        jobViewTotalItems: data?.totalElements,
        jobViewCurrentPage: 0,
        jobViewItemsPerPage: newSize,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to fetch job tasks data.");
      console.error("Error fetching job tasks:", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <DetailsModal
      modalHeader={
        <JobViewModalHeader
          selectedJob={selectedJob}
          jobTasks={jobViewTasks}
          pendingStatuses={pendingStatuses}
        />
      }
      isOpen={isJobViewModalOpen}
      onClose={onCloseModal}
      footer={
        <PaginationBar
          totalPages={jobViewTotalPages}
          currentPage={jobViewCurrentPage}
          totalItems={jobViewTotalItems}
          itemsPerPage={jobViewItemsPerPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      }
    >
      <JobViewGrid />
    </DetailsModal>
  );
};
